/**
 * Enhanced logging utility for POS system debugging
 * Provides consistent formatting and categorization for all log messages
 */

export interface LogData {
  [key: string]: any
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success'

export interface LogEntry {
  timestamp: string
  level: LogLevel
  component: string
  action: string
  message: string
  data?: LogData
  stack?: string
}

class Logger {
  private logs: LogEntry[] = []
  private maxLogs = 1000 // Keep last 1000 log entries
  
  // Check if console logging is enabled (disabled in production)
  private get isConsoleEnabled(): boolean {
    return import.meta.env.DEV
  }

  private formatMessage(component: string, action: string, message: string): string {
    const timestamp = new Date().toISOString().split('T')[1]?.split('.')[0] || ''
    return `[${timestamp}] 🛒 [${component}-${action}] ${message}`
  }

  private addToHistory(level: LogLevel, component: string, action: string, message: string, data?: LogData, error?: Error) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component,
      action,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : undefined,
      stack: error?.stack
    }

    this.logs.push(entry)
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }
  }

  private logWithLevel(level: LogLevel, component: string, action: string, message: string, data?: LogData, error?: Error) {
    const formattedMessage = this.formatMessage(component, action, message)
    const emoji = this.getLevelEmoji(level)
    
    this.addToHistory(level, component, action, message, data, error)

    // Only output to console if enabled (disabled in production)
    if (this.isConsoleEnabled) {
      switch (level) {
        case 'debug':
          console.debug(`🔍 ${formattedMessage}`, data || '')
          break
        case 'info':
          console.log(`ℹ️ ${formattedMessage}`, data || '')
          break
        case 'warn':
          console.warn(`⚠️ ${formattedMessage}`, data || '')
          break
        case 'error':
          console.error(`❌ ${formattedMessage}`, data || '', error || '')
          break
        case 'success':
          console.log(`✅ ${formattedMessage}`, data || '')
          break
      }
    }
  }

  private getLevelEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍'
      case 'info': return 'ℹ️'
      case 'warn': return '⚠️'
      case 'error': return '❌'
      case 'success': return '✅'
      default: return '📝'
    }
  }

  debug(component: string, action: string, message: string, data?: LogData) {
    this.logWithLevel('debug', component, action, message, data)
  }

  info(component: string, action: string, message: string, data?: LogData) {
    this.logWithLevel('info', component, action, message, data)
  }

  warn(component: string, action: string, message: string, data?: LogData) {
    this.logWithLevel('warn', component, action, message, data)
  }

  error(component: string, action: string, message: string, data?: LogData, error?: Error) {
    this.logWithLevel('error', component, action, message, data, error)
  }

  success(component: string, action: string, message: string, data?: LogData) {
    this.logWithLevel('success', component, action, message, data)
  }

  // Specialized logging methods for common POS operations
  cart(component: string, action: string, beforeState?: LogData, afterState?: LogData) {
    if (this.isConsoleEnabled) {
      console.group(`🛒 [CART-${action}] ${component}`)
      if (beforeState) console.log('Before:', beforeState)
      if (afterState) console.log('After:', afterState)
      console.groupEnd()
    }
    
    this.addToHistory('info', component, `CART-${action}`, 'Cart state change', {
      before: beforeState,
      after: afterState
    })
  }

  api(component: string, endpoint: string, method: string, payload?: LogData, response?: LogData) {
    if (this.isConsoleEnabled) {
      console.group(`🌐 [API-${method}] ${component} -> ${endpoint}`)
      if (payload) console.log('Request:', payload)
      if (response) console.log('Response:', response)
      console.groupEnd()
    }
    
    this.addToHistory('info', component, `API-${method}`, `${endpoint}`, {
      endpoint,
      method,
      payload,
      response
    })
  }

  // Performance logging
  performance(component: string, action: string, duration: number, data?: LogData) {
    const message = `${action} completed in ${duration}ms`
    this.info(component, 'PERFORMANCE', message, { ...data, duration })
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(-count)
  }

  // Get logs by component
  getLogsByComponent(component: string, count: number = 50): LogEntry[] {
    return this.logs.filter(log => log.component === component).slice(-count)
  }

  // Get logs by level
  getLogsByLevel(level: LogLevel, count: number = 50): LogEntry[] {
    return this.logs.filter(log => log.level === level).slice(-count)
  }

  // Export logs for debugging
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2)
  }

  // Clear log history
  clearLogs() {
    this.logs = []
    if (this.isConsoleEnabled) {
      console.log('🧹 Logger: Log history cleared')
    }
  }

  // Print current log summary
  summary() {
    if (this.isConsoleEnabled) {
      console.group('📊 Logger Summary')
      console.log(`Total logs: ${this.logs.length}`)
      
      const levelCounts = this.logs.reduce((acc, log) => {
        acc[log.level] = (acc[log.level] || 0) + 1
        return acc
      }, {} as Record<LogLevel, number>)
      
      console.log('By level:', levelCounts)
      
      const componentCounts = this.logs.reduce((acc, log) => {
        acc[log.component] = (acc[log.component] || 0) + 1
        return acc
      }, {} as Record<string, number>)
      
      console.log('By component:', componentCounts)
      console.groupEnd()
    }
  }
}

// Create global logger instance
export const logger = new Logger()

// Make logger available in window for debugging
if (typeof window !== 'undefined') {
  (window as any).posLogger = logger
}

// Export convenience functions
export const logDebug = (component: string, action: string, message: string, data?: LogData) => 
  logger.debug(component, action, message, data)

export const logInfo = (component: string, action: string, message: string, data?: LogData) => 
  logger.info(component, action, message, data)

export const logWarn = (component: string, action: string, message: string, data?: LogData) => 
  logger.warn(component, action, message, data)

export const logError = (component: string, action: string, message: string, data?: LogData, error?: Error) => 
  logger.error(component, action, message, data, error)

export const logSuccess = (component: string, action: string, message: string, data?: LogData) => 
  logger.success(component, action, message, data)

export const logCart = (component: string, action: string, beforeState?: LogData, afterState?: LogData) => 
  logger.cart(component, action, beforeState, afterState)

export const logApi = (component: string, endpoint: string, method: string, payload?: LogData, response?: LogData) => 
  logger.api(component, endpoint, method, payload, response)

export const logPerformance = (component: string, action: string, duration: number, data?: LogData) => 
  logger.performance(component, action, duration, data)

export default logger